import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Mail, X } from "lucide-react";
import {
    getProspectionVariables,
    ProspectionVariables,
} from "interfaces/restaurantNews";
import { getEmailTemplates, createEmailTemplate } from "interfaces/emailTemplate";
import { generateEmailComposeUrl, getUserEmailProvider, getEmailProviderDisplayName } from "utils/emailProviders";

// ─── prospection template store ────────────────────────────────────────────────
// These 3 are seeded (once, per user) into the CRM's real EmailTemplate collection the
// first time this modal runs — from then on they're ordinary saved templates: editable and
// deletable from /email-templates like anything else, and reused here alongside whatever
// other templates the user already has. `name` doubles as the subject line, `text` as the
// body — same convention as ComposeEmail.tsx, including the single-brace {token} syntax, so
// a template edited here behaves identically wherever else it's applied in the CRM.
//
// The actual offer/pitch is left as a bracketed placeholder — this CRM serves many different
// sales teams (equipment, financing, software, staffing…) prospecting the same restaurant
// database, so the templates can't assume what any given rep sells.

interface EmailTemplateDoc {
    _id: string;
    name: string;
    description?: string;
    text: string;
    type?: string;
    lang?: "en" | "fr";
}

const SEED_TEMPLATES: Array<{ name: string; description: string; text: string }> = [
    {
        name: "Félicitations pour l'ouverture de {restaurantName} !",
        description: "Modèle de prospection — restaurant qui vient d'ouvrir (ton chaleureux)",
        text:
`Bonjour {ownerFirstName},

Toutes mes félicitations pour l'ouverture de {restaurantName} à {city} ! Je suis tombé(e) sur l'article « {articleTitle} » et je tenais à vous adresser mes meilleurs vœux de réussite pour cette nouvelle aventure.

Je m'appelle {repFirstName}, je travaille chez {repCompany} et j'accompagne les restaurateurs qui viennent d'ouvrir sur [votre offre]. Si vous êtes disponible, je serais ravi(e) d'échanger 15 minutes pour voir comment nous pourrions vous être utiles dans cette phase de lancement.

Belle continuation,
{repFullName}
{repEmail}`,
    },
    {
        name: "{restaurantName} — quelques mots avant votre lancement",
        description: "Modèle de prospection — restaurant qui vient d'ouvrir (présentation courte)",
        text:
`Bonjour {ownerFirstName},

Félicitations pour l'ouverture de {restaurantName} ({city}) !

En bref : [votre offre en une phrase]. Beaucoup de restaurateurs qui viennent d'ouvrir nous sollicitent sur ce sujet dans les premiers mois — seriez-vous disponible pour un rapide échange cette semaine ?

Cordialement,
{repFullName}
{repCompany} — {repEmail}`,
    },
    {
        name: "On a vu l'ouverture de {restaurantName} à {city}",
        description: "Modèle de prospection — restaurant qui vient d'ouvrir (suivi actualité)",
        text:
`Bonjour {ownerFirstName},

On suit l'actualité des ouvertures CHR et « {articleTitle} » a attiré notre attention — félicitations pour le lancement de {restaurantName} !

Je me permets de vous contacter au sujet de [votre offre]. N'hésitez pas à me dire si c'est le bon moment, ou si je dois revenir vers vous plus tard.

Bien à vous,
{repFullName}
{repEmail}`,
    },
];

const TOKENS: Array<{ key: keyof ProspectionVariables; label: string }> = [
    { key: "restaurantName", label: "Restaurant" },
    { key: "ownerFirstName", label: "Prénom contact" },
    { key: "city", label: "Ville" },
    { key: "openingDate", label: "Date d'ouverture" },
    { key: "articleTitle", label: "Titre article" },
    { key: "repFirstName", label: "Mon prénom" },
    { key: "repFullName", label: "Mon nom complet" },
    { key: "repCompany", label: "Mon entreprise" },
    { key: "repEmail", label: "Mon email" },
    { key: "repPhone", label: "Mon téléphone" },
];

function renderTemplate(str: string, vars: ProspectionVariables): string {
    const dict: Record<string, string> = { ...vars };
    return (str || "").replace(/\{(\w+)\}/g, (_, key) => dict[key] ?? "");
}

const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

export function ProspectionEmailModal({
    articleId,
    establishmentName,
    onClose,
}: {
    articleId: string;
    establishmentName: string;
    onClose: () => void;
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [variables, setVariables] = useState<ProspectionVariables | null>(null);
    const [templates, setTemplates] = useState<EmailTemplateDoc[]>([]);
    const [templateId, setTemplateId] = useState<string | null>(null);
    const [to, setTo] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [providerLabel, setProviderLabel] = useState("votre messagerie");
    const [lastFocused, setLastFocused] = useState<"subject" | "body">("body");

    const subjectRef = useRef<HTMLInputElement>(null);
    const bodyRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const [ctx, existing] = await Promise.all([
                    getProspectionVariables(articleId),
                    getEmailTemplates().catch(() => [] as EmailTemplateDoc[]),
                ]);
                if (cancelled) return;

                // Seed the 3 pre-built templates into the user's real template store the
                // first time — after this they're indistinguishable from any other saved
                // template (editable/deletable from /email-templates).
                const existingList: EmailTemplateDoc[] = Array.isArray(existing) ? existing : [];
                const missing = SEED_TEMPLATES.filter((seed) => !existingList.some((t) => t.name === seed.name));
                const created = await Promise.all(
                    missing.map((seed) =>
                        createEmailTemplate({ ...seed, type: "prospection", lang: "fr" }).catch(() => null)
                    )
                );
                if (cancelled) return;

                const allTemplates = [...existingList, ...created.filter((t): t is EmailTemplateDoc => !!t)];
                setTemplates(allTemplates);
                setVariables(ctx.variables);
                setTo(ctx.establishmentEmail || "");

                const first = allTemplates.find((t) => t.name === SEED_TEMPLATES[0].name) ?? allTemplates[0];
                if (first) applyTemplateDoc(first, ctx.variables);
            } catch {
                if (!cancelled) setError("Impossible de charger les informations de l'établissement");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articleId]);

    const applyTemplateDoc = (tpl: EmailTemplateDoc, vars: ProspectionVariables) => {
        setTemplateId(tpl._id);
        setSubject(renderTemplate(tpl.name, vars));
        setBody(renderTemplate(tpl.text, vars));
    };

    const insertToken = (key: keyof ProspectionVariables) => {
        const token = `{${key}}`;
        if (lastFocused === "subject" && subjectRef.current) {
            const el = subjectRef.current;
            const start = el.selectionStart ?? subject.length;
            const end = el.selectionEnd ?? subject.length;
            const next = subject.slice(0, start) + token + subject.slice(end);
            setSubject(next);
            requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
        } else if (bodyRef.current) {
            const el = bodyRef.current;
            const start = el.selectionStart ?? body.length;
            const end = el.selectionEnd ?? body.length;
            const next = body.slice(0, start) + token + body.slice(end);
            setBody(next);
            requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
        }
    };

    const handleSend = async () => {
        if (!to.trim() || !subject.trim() || !body.trim()) {
            setError("Destinataire, objet et message sont requis");
            return;
        }
        setSending(true);
        setError(null);
        try {
            // Opens the rep's own mailbox pre-filled, same pattern as the rest of the CRM's
            // compose flow (ComposeEmail.tsx) — the mail actually goes out from their real
            // inbox, not a shared sender, so replies land where the rep will see them.
            const provider = await getUserEmailProvider();
            setProviderLabel(getEmailProviderDisplayName(provider));
            const url = generateEmailComposeUrl(provider, { to: to.trim(), subject: subject.trim(), body });
            window.open(url, "_blank");
            setSent(true);
        } catch {
            setError("Impossible d'ouvrir votre messagerie — réessayez");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
            <div className="relative w-full max-w-xl bg-surface border border-border rounded-2xl shadow-float max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-ink-muted" />
                        <h2 className="font-sans text-sm font-semibold text-ink">Contacter {establishmentName}</h2>
                    </div>
                    <button onClick={onClose} className={`p-1 text-ink-faint hover:text-ink rounded transition-colors ${FOCUS_RING}`}>
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {loading ? (
                    <div className="px-5 py-10 text-center text-sm text-ink-muted">Chargement…</div>
                ) : sent ? (
                    <div className="px-5 py-10 flex flex-col items-center text-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-success-subtle text-success flex items-center justify-center">
                            <Check className="w-5 h-5" />
                        </div>
                        <p className="text-sm font-medium text-ink">Ouvert dans {providerLabel}</p>
                        <p className="text-xs text-ink-muted max-w-xs">
                            Le message vers {to} est prêt dans un nouvel onglet, avec l'objet et le contenu déjà remplis — il ne reste qu'à cliquer sur envoyer depuis votre messagerie.
                        </p>
                        <button onClick={onClose} className={`mt-2 text-xs font-medium text-ink-muted hover:text-ink rounded ${FOCUS_RING}`}>
                            Fermer
                        </button>
                    </div>
                ) : variables ? (
                    <>
                        <div className="overflow-y-auto px-5 py-4 space-y-4">
                            {/* template picker — every template the user has saved, incl. the 3 pre-built ones */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <p className="text-[10px] text-ink-faint uppercase tracking-wide font-semibold">Modèle</p>
                                    <a href="/email-templates" target="_blank" rel="noopener noreferrer"
                                        className="text-[10px] text-ink-faint hover:text-ink transition-colors">
                                        Gérer mes modèles →
                                    </a>
                                </div>
                                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                    {templates.filter((t) => t.type !== "sms" && t.text).map((tpl) => (
                                        <button
                                            key={tpl._id}
                                            onClick={() => applyTemplateDoc(tpl, variables)}
                                            title={tpl.description || tpl.name}
                                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors duration-150 max-w-[220px] truncate ${FOCUS_RING} ${
                                                templateId === tpl._id
                                                    ? "bg-ink text-ink-inverse border-transparent"
                                                    : "bg-surface text-ink-muted border-border hover:bg-surface-sunken"
                                            }`}
                                        >
                                            {tpl.name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* to */}
                            <div>
                                <label className="text-[10px] text-ink-faint uppercase tracking-wide font-semibold mb-1 block">Destinataire</label>
                                <input
                                    type="email"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    placeholder="contact@restaurant.fr"
                                    className={`w-full text-sm border border-border rounded-lg px-3 py-2 bg-surface text-ink placeholder:text-ink-faint ${FOCUS_RING}`}
                                />
                            </div>

                            {/* subject */}
                            <div>
                                <label className="text-[10px] text-ink-faint uppercase tracking-wide font-semibold mb-1 block">Objet</label>
                                <input
                                    ref={subjectRef}
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    onFocus={() => setLastFocused("subject")}
                                    className={`w-full text-sm border border-border rounded-lg px-3 py-2 bg-surface text-ink ${FOCUS_RING}`}
                                />
                            </div>

                            {/* body */}
                            <div>
                                <label className="text-[10px] text-ink-faint uppercase tracking-wide font-semibold mb-1 block">Message</label>
                                <textarea
                                    ref={bodyRef}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    onFocus={() => setLastFocused("body")}
                                    rows={10}
                                    className={`w-full text-sm border border-border rounded-lg px-3 py-2 bg-surface text-ink leading-relaxed resize-y ${FOCUS_RING}`}
                                />
                            </div>

                            {/* variable tokens */}
                            <div>
                                <p className="text-[10px] text-ink-faint uppercase tracking-wide font-semibold mb-1.5">Insérer une variable</p>
                                <div className="flex flex-wrap gap-1">
                                    {TOKENS.map((t) => (
                                        <button
                                            key={t.key}
                                            onClick={() => insertToken(t.key)}
                                            title={variables[t.key] || "(vide)"}
                                            className={`text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-sunken text-ink-muted hover:bg-border hover:text-ink transition-colors ${FOCUS_RING}`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {error && <p className="text-xs text-danger">{error}</p>}
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border flex-shrink-0">
                            <button onClick={onClose} disabled={sending}
                                className={`text-xs font-medium text-ink-muted hover:text-ink px-3 py-1.5 rounded-lg transition-colors ${FOCUS_RING}`}>
                                Annuler
                            </button>
                            <button onClick={handleSend} disabled={sending}
                                className={`inline-flex items-center gap-1.5 text-xs font-medium bg-ink text-ink-inverse px-3.5 py-1.5 rounded-lg hover:bg-ink/90 transition-colors disabled:opacity-50 ${FOCUS_RING}`}>
                                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                {sending ? "Envoi…" : "Envoyer"}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="px-5 py-10 text-center text-sm text-danger">{error}</div>
                )}
            </div>
        </div>
    );
}
